import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";

import "./ReservationSchedulerNew.css";
import { AppContext } from "../../../context/AppContext";
import { toast } from "react-toastify";

const getTodayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateTimeString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getTomorrowString = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const day = String(tomorrow.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseYmdHmsToLocal = (s) => {
  if (!s) return null;
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (!m) return null;

  const [_, y, mo, d, h = "0", mi = "0", s2 = "0"] = m;
  return new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s2),
  );
};

const PRESET_REASONS = [
  "Fully booked",
  "Staff shortage",
  "Private event",
  "Kitchen maintenance",
  "Public holiday",
  "Special occasion",
];

export default function ClosureDrawer({
  selectedDate,
  isClosed,
  onCancel,
  onConfirm,
  selectedClosure,
  setIsClosed,
}) {
  const {
    createNewClosure,
    reservationLoading,
    getSettings,
    removeClosure,
  } = useContext(AppContext);
  const [mode, setMode] = useState("today");
  const [start, setStart] = useState("00:00");
  const [end, setEnd] = useState("23:59");
  const [reason, setReason] = useState("");
  const [isError, setIsError] = useState(false);
  const [fullDayDate, setFullDayDate] = useState("");

  const [closureData, setClosureData] = useState({
    startTime: new Date(),
    endTime: new Date(),
    status: "Active",
    reason: "",
    source: "REACT",
  });

  const dateObj = new Date(selectedDate);
  const longLabel = dateObj.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  useEffect(() => {
    if (isClosed) {
      setMode("full");
      return;
    }

    if (selectedClosure) {
      setMode("range");

      const start = parseYmdHmsToLocal(selectedClosure.startTime);
      const end = parseYmdHmsToLocal(selectedClosure.endTime);

      setClosureData({
        startTime: selectedClosure.startTime,
        endTime: selectedClosure.endTime,
        status: selectedClosure.status,
        reason: selectedClosure.reason,
        source: selectedClosure.source,
      });
    }
  }, [isClosed, selectedClosure]);

  useEffect(() => {
    if (!selectedDate) return;

    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(23, 59, 59, 0);

    setClosureData((prev) => ({
      ...prev,
      startTime: start,
      endTime: end,
    }));
  }, [selectedDate]);

  useEffect(() => {
    if (mode !== "today") return;

    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    now.setSeconds(0);
    now.setMilliseconds(0);

    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    setClosureData((prev) => ({
      ...prev,
      startTime: now,
      endTime: endOfToday,
    }));
  }, [mode]);

  const toLocalDate = (input) => {
    if (!input) return null;

    if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [y, m, d] = input.split("-").map(Number);
      return new Date(y, m - 1, d);
    }

    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDateTimeLocal = (input) => {
    const d = toLocalDate(input);
    if (!d) return "";

    const pad = (n) => String(n).padStart(2, "0");

    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const formatToCustomDate = (dateInput) => {
    if (!dateInput) return "";

    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");

    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());

    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "todaytime") {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);

      setClosureData((prev) => ({
        ...prev,
        startTime: value,
        endTime: endOfToday,
      }));
      return;
    }

    if (name === "fullDayDate") {
      // For full day closure
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);

      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      setFullDayDate(value);
      setClosureData((prev) => ({
        ...prev,
        startTime: selectedDate,
        endTime: endDate,
      }));
      return;
    }

    const selected = new Date(value);

    if (name === "startTime") {
      const end = new Date(selected.getTime() + 24 * 60 * 60 * 1000);

      setClosureData((prev) => ({
        ...prev,
        startTime: value,
        endTime: end,
      }));
    } else {
      setClosureData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateClosureData = (data) => {
    const { startTime, endTime, reason } = data;

    if (!startTime || !endTime || !reason) {
      return false;
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return false;
    }

    if (start >= end) {
      toast.error("End time must be after start time.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { startTime, endTime, ...rest } = closureData;

    const formattedData = {
      startTime: formatToCustomDate(startTime),
      endTime: formatToCustomDate(endTime),
      ...rest,
    };

    const closureId = selectedClosure?.id;

    if (isClosed && closureId) {
      await removeClosure(closureId, {
        onSuccess: (res) => {
          toast.success("Reservation has been re-opened successfully!");
          onCancel();
          getSettings();
          setIsClosed(false);
          setClosureData({
            startTime: new Date(),
            endTime: new Date(),
            status: "Active",
            reason: "",
            source: "REACT",
          });
          setReason("");
        },
        onFailed: (err) => {
          console.error("Error on removing closure", err);
        },
      });
      return;
    }

    const isVaid = validateClosureData(closureData);
    setIsError(!isVaid);

    if (!isVaid) return;
    await createNewClosure(formattedData, {
      onSuccess: (res) => {
        toast.success("Reservation has been closed successfully!");
        onCancel();
        setReason("");
        getSettings();
        setClosureData({
          startTime: new Date(),
          endTime: new Date(),
          status: "Active",
          reason: "",
          source: "REACT",
        });
      },
      onFailed: (err) => {
        console.error("Error on creating new closure", err?.messages);
        const errMsg = err?.messages || "Failed to create new closure.";
        toast.error(errMsg);
      },
    });
  };
  const min = getTodayDateTimeString();
  const minDate = getTomorrowString();
  const [isMobileDrawer, setIsMobileDrawer] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsMobileDrawer(mq.matches);
    apply();
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  useEffect(() => {
    if (!isMobileDrawer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileDrawer]);

  const panelInner = (
    <>
      <div className="rs-closurePanel__header">
        <div>
          <h2 className="rs-closurePanel__title" id="rs-closure-title">
            Manage Closure for <br />
            <span className="rs-closurePanel__titleDate">{longLabel}</span>
          </h2>
        </div>
        <button
          type="button"
          className="rs-closurePanel__close"
          onClick={onCancel}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>
      </div>

      <form className="rs-closurePanel__body" onSubmit={handleSubmit}>
        {!isClosed && (
          <>
            {/* Scope toggle */}
            <section className="rs-closurePanel__section">
              <div className="rs-fieldLabel">Closure Scope</div>
              <div className="rs-segment">
                <button
                  type="button"
                  className={
                    "rs-segment__btn" +
                    (mode === "today" ? " rs-segment__btn--active" : "")
                  }
                  onClick={() => setMode("today")}
                >
                  Today
                </button>
                <button
                  type="button"
                  className={
                    "rs-segment__btn" +
                    (mode === "full" ? " rs-segment__btn--active" : "")
                  }
                  onClick={() => setMode("full")}
                >
                  Full day
                </button>
                <button
                  type="button"
                  className={
                    "rs-segment__btn" +
                    (mode === "range" ? " rs-segment__btn--active" : "")
                  }
                  onClick={() => setMode("range")}
                >
                  Specific time window
                </button>
              </div>

              <p className="rs-closurePanel__hint">
                Use this panel to close reservations for the full day or a
                specific time window. Existing bookings are not affected.
              </p>

              {mode === "today" && (
                <div>
                  {/* <div className="rs-timeField">
                    <label className="rs-fieldLabel">From</label>
                    <input
                      type="datetime-local"
                      className="rs-input"
                      name="todaytime"
                      value={formatDateTimeLocal(closureData?.startTime)}
                      onChange={handleChange}
                      min={min}
                    />
                  </div> */}
                  <p
                    className="rs-closurePanel__hint"
                    style={{ marginTop: "8px", fontSize: "0.875rem" }}
                  >
                    Closes reservations from now until end of today (11:59 PM).
                  </p>
                </div>
              )}

              {mode === "full" && (
                <div>
                  <div className="rs-timeField">
                    <label className="rs-fieldLabel">Select Date</label>
                    <input
                      type="date"
                      className="rs-input"
                      name="fullDayDate"
                      value={fullDayDate}
                      onChange={handleChange}
                      min={getTomorrowString()}
                    />
                  </div>
                  <p
                    className="rs-closurePanel__hint"
                    style={{ marginTop: "8px", fontSize: "0.875rem" }}
                  >
                    Select a future date to close for the entire day.
                  </p>
                </div>
              )}
              {mode === "range" && (
                <div>
                  <div className="rs-timeField">
                    <label className="rs-fieldLabel">From</label>
                    <input
                      type="datetime-local"
                      className="rs-input"
                      name="startTime"
                      value={formatDateTimeLocal(closureData?.startTime)}
                      onChange={handleChange}
                      min={min}
                    />
                  </div>
                  <div className="rs-timeField">
                    <label className="rs-fieldLabel">To</label>
                    <input
                      type="datetime-local"
                      className="rs-input"
                      name="endTime"
                      value={formatDateTimeLocal(closureData?.endTime)}
                      onChange={handleChange}
                      min={min}
                    />
                  </div>
                </div>
              )}
            </section>

            {/* Reason */}
            <section className="rs-closurePanel__section">
              <label className="rs-fieldLabel">Reason</label>
              <div className="rs-presetReasons">
                {PRESET_REASONS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={
                      "rs-presetReasons__chip" +
                      (closureData?.reason === preset
                        ? " rs-presetReasons__chip--active"
                        : "")
                    }
                    onClick={() =>
                      setClosureData((prev) => ({
                        ...prev,
                        reason: prev.reason === preset ? "" : preset, // toggle off if already selected
                      }))
                    }
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <textarea
                className={
                  isError && closureData?.reason.length === 0
                    ? "rs-input rs-input--error"
                    : "rs-input"
                }
                name="reason"
                rows={3}
                placeholder="Other: Enter here..."
                value={closureData?.reason}
                onChange={handleChange}
              />
              {isError && closureData?.reason.length === 0 && (
                <p className="rs-fieldError">Reason is required.</p>
              )}
            </section>
          </>
        )}

        {isClosed && (
          <section className="rs-closurePanel__section rs-closurePanel__section--info">
            This date is currently <strong>closed</strong> for new reservations.
            You can re-open it for bookings.
          </section>
        )}

        <div className="rs-closurePanel__footer">
          <button
            type="button"
            className="rs-btn rs-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={
              "rs-btn " +
              (isClosed ? "rs-btn--primary" : "rs-btn--dangerPrimary")
            }
            disabled={reservationLoading}
          >
            {reservationLoading
              ? "Processing..."
              : isClosed
                ? "Re-open Date"
                : "Close Reservations"}
          </button>
        </div>
      </form>
    </>
  );

  if (isMobileDrawer && typeof document !== "undefined") {
    return createPortal(
      <>
        <div
          className="rs-closureBackdrop"
          onClick={onCancel}
          aria-hidden="true"
        />
        <aside
          className="rs-closurePanel rs-closurePanel--drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rs-closure-title"
        >
          {panelInner}
        </aside>
      </>,
      document.body
    );
  }

  return (
    <aside
      className="rs-closurePanel"
      role="dialog"
      aria-labelledby="rs-closure-title"
    >
      {panelInner}
    </aside>
  );
}
